<script setup lang="ts">
import { ref } from 'vue';
import { useAutocomplete } from '@/composables/useAutocomplete';

/**
 * Champ d'autocomplétion sur la liste de stations.
 * Émet `pick(stationId)` quand un item est sélectionné.
 *
 * On reset l'input après pick — c'est le comportement attendu pour
 * "ajouter une étape", car l'utilisateur va probablement en ajouter d'autres.
 */
const emit = defineEmits<{
  pick: [id: string];
}>();

defineProps<{
  placeholder?: string;
}>();

const { query, matches, open, update, close, reset } = useAutocomplete();
const inputRef = ref<HTMLInputElement | null>(null);

function onPick(id: string) {
  emit('pick', id);
  reset();
}

// `mousedown` plutôt que `click` pour éviter que blur ferme la liste avant le click
function onSugMouseDown(e: MouseEvent, id: string) {
  e.preventDefault();
  onPick(id);
}

function onBlur() {
  // Petit délai : laisse le temps au mousedown de tirer si l'utilisateur tape sur une sug
  setTimeout(close, 200);
}
</script>

<template>
  <div class="relative">
    <div
      class="flex items-center gap-2 px-3 py-2.5 bg-surface border border-DEFAULT rounded-md focus-within:border-accent transition-colors duration-160"
    >
      <i class="ti ti-plus text-text-faint" aria-hidden="true"></i>
      <input
        ref="inputRef"
        type="text"
        :value="query"
        :placeholder="placeholder || 'Ajouter une étape…'"
        autocomplete="off"
        class="flex-1 bg-transparent outline-none text-sm"
        @input="update(($event.target as HTMLInputElement).value)"
        @focus="update(query)"
        @blur="onBlur"
      />
    </div>

    <ul
      v-if="open"
      class="absolute z-30 left-0 right-0 mt-1 bg-surface border border-DEFAULT rounded-md shadow-lg max-h-64 overflow-y-auto"
      role="listbox"
    >
      <li
        v-for="s in matches"
        :key="s.id"
        class="flex items-center gap-2 px-3 py-2 text-sm cursor-pointer hover:bg-surface-muted transition-colors duration-160"
        role="option"
        @mousedown="(e) => onSugMouseDown(e, s.id)"
      >
        <i class="ti ti-map-pin text-text-faint" aria-hidden="true"></i>
        <span>{{ s.name }}</span>
      </li>
    </ul>
  </div>
</template>
